/* components/HomePage.js */

import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
// import Weather from './Weather';
import useTranslation from 'next-translate/useTranslation';

import dynamic from 'next/dynamic';
import Spinner from './Spinner';
import Pulse from './Pulse';
import FormattedDate from './FormattedDate';
import SecurityOpeningNote from './SecurityOpeningNote';
import RelatedLinks from './RelatedLinks';
import TravelPoll from './TravelPoll';
import DisruptionBanner from './DisruptionBanner';

// Dynamically import components
const FlightTable = dynamic(() => import('./FlightTable'));
const RecentWaitChart = dynamic(() => import('./RecentWaitChart'), {
    ssr: false
});

function HomePage({
    waitTime,
    lastUpdated,
    error,
    delayedFlights,
    passportTimes,
    graphData,
    lastUpdatedUpdated,
    securityOpening,
    specialWarning,
    timezone
    // weatherData
}) {
    const { t, lang } = useTranslation('home');
    const [flashClass, setFlashClass] = useState('');

    useEffect(() => {
        if (lastUpdatedUpdated) {
            // Intentional synchronous setState to trigger flash animation
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setFlashClass('flash');

            const timer = setTimeout(() => {
                setFlashClass('');
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [lastUpdatedUpdated]);

    // Track errors when they occur
    useEffect(() => {
        if (error && typeof umami !== 'undefined') {
            umami.track('error-occurred', {
                errorMessage: error,
                locale: lang,
                timestamp: new Date().toISOString()
            });
        }
    }, [error, lang]);

    return (
        <div className="px-8 h-full">
            <DisruptionBanner warning={specialWarning} />
            <Head>
                <title>{t('common:title')}</title>
                {/* <script
                    async
                    src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2658857150579605"
                    crossOrigin="anonymous"
                ></script> */}
            </Head>

            <main className="main flex flex-col justify-center items-center text-center mb-10 h-full">
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 rounded p-5 font-bold text-xl">
                        {error}
                    </div>
                )}
                {!error && (
                    <>
                        <div className="mt-4 md:mt-[4rem] flex-grow">
                            <div className="flex flex-col flex-1">
                                <div className="m-auto flex flex-col">
                                    <h1 className="m-4 text-2xl center leading-9">
                                        {t('current_wait') + ':'}
                                    </h1>
                                    {waitTime && lastUpdated ? (
                                        <div>
                                            <div className="text-6xl my-3">
                                                {waitTime?.replace(' min', '')}{' '}
                                                {t('common:minutes')}
                                            </div>
                                            {/* One security control serves both
                                                terminals, so say so: T3 flyers
                                                otherwise have no way to know the
                                                number is theirs too. */}
                                            <div className="text-xs uppercase tracking-wide opacity-60">
                                                {t(
                                                    'common:current_wait_terminals'
                                                )}
                                            </div>
                                            <div
                                                className={`${flashClass} flex items-center justify-center gap-2 text-sm rounded`}
                                            >
                                                <Pulse className="m-0" />
                                                <span>
                                                    {t('common:last_updated')}
                                                </span>
                                                <span className="opacity-40">
                                                    ·
                                                </span>
                                                <FormattedDate
                                                    date={lastUpdated}
                                                    locale={lang}
                                                    timeZone={timezone}
                                                    showDate={false}
                                                    showSeconds={true}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex h-[108px] items-center justify-center">
                                            <Spinner />
                                        </div>
                                    )}
                                    {waitTime && lastUpdated && (
                                        <Link
                                            href="/when-to-arrive"
                                            className="mt-6 self-center rounded-full border-2 border-[var(--yellow)] px-6 py-3 font-semibold text-[var(--yellow)] transition hover:bg-[var(--yellow)]/10"
                                            onClick={() => {
                                                if (
                                                    typeof umami !== 'undefined'
                                                ) {
                                                    umami.track(
                                                        'when-to-arrive-cta'
                                                    );
                                                }
                                            }}
                                        >
                                            {t('when_to_arrive_cta')} →
                                        </Link>
                                    )}
                                    {graphData && graphData.length > 1 && (
                                        <div className="mt-10">
                                            <RecentWaitChart data={graphData} />
                                        </div>
                                    )}

                                    {passportTimes &&
                                        passportTimes !== 'error' && (
                                            <Link
                                                href="/passport-control"
                                                className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-1 rounded-full bg-[var(--yellow)]/10 hover:bg-[var(--yellow)]/20 transition px-5 py-2.5 text-sm"
                                            >
                                                <span className="font-semibold">
                                                    {t('passport_short')}
                                                </span>
                                                {passportTimes.C?.text && (
                                                    <span>
                                                        <span className="opacity-50">
                                                            {t('gates_c')}{' '}
                                                        </span>
                                                        {passportTimes.C.text}
                                                    </span>
                                                )}
                                                {passportTimes.F?.text && (
                                                    <span>
                                                        <span className="opacity-50">
                                                            {t('gates_ef')}{' '}
                                                        </span>
                                                        {passportTimes.F.text}
                                                    </span>
                                                )}
                                            </Link>
                                        )}
                                </div>
                            </div>

                            <SecurityOpeningNote
                                securityOpening={securityOpening}
                            />

                            {/* Below the wait time and the chart on purpose.
                                People come here to read one number in three
                                seconds; a question above that would tax the
                                thing the site is for. */}
                            <TravelPoll />

                            <FlightTable flights={delayedFlights} />

                            <RelatedLinks
                                links={[
                                    {
                                        href: '/blog/best-time-to-pass-cph-security',
                                        labelKey: 'link_best_time'
                                    },
                                    {
                                        href: '/blog/how-early-to-arrive-at-cph',
                                        labelKey: 'link_how_early'
                                    },
                                    {
                                        href: '/travel-times',
                                        labelKey: 'link_wait_by_day'
                                    },
                                    {
                                        href: '/blog/cph-busiest-times',
                                        labelKey: 'link_busiest'
                                    },
                                    { href: '/faq', labelKey: 'link_faq' }
                                ]}
                            />

                            {/* <button
                                className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition text-sm"
                                onClick={() => {
                                    if (typeof umami !== 'undefined') {
                                        umami.track('cph-airport-map');
                                    }
                                    window.location.href =
                                        '/cph-airport_map-after-security-v02-june2023.pdf';
                                }}
                            >
                                {t('common:map_over_cph_airport')}
                            </button> */}

                            {/* <Weather weatherData={weatherData} /> */}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

export default HomePage;
